import BreadCrumbs from 'components/breadcrumbs'
import LihatDetail from 'components/Icons/LihatDetail'
import PMBPagination from 'components/PMBPagination'
import PMBSelect from 'components/PMBSelect'
import { TableComponent as DataTable } from 'components/TableComponent'
import { useRef, useState } from 'react'
import { ChevronDown, Search, X } from 'react-feather'
import { useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import {
  Badge,
  Card,
  CardBody,
  Input,
  InputGroup,
  InputGroupText,
} from 'reactstrap'
import { newFormatThousandSeparator } from 'utils/formatNumber'
import { URL_INVENTORI_INTELLIGENCE } from 'utils/pathUrl'
import {
  useFetchInventoriIntelligentQuery,
  useFetchWarehouseNetworkItemsQuery,
} from './inventoriIntelligentApiSlice'
import {
  removeItemFilter,
  setFetchOption,
  setItemsFilter,
  setOwnershipFilter,
} from './inventoriIntelligentSlice'
import './styles.css'

const InventoriIntelligent = () => {
  const fetchTimeoutRef = useRef(0)
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const intl = useIntl()
  const { tag, ownershipFilter, itemsFilter } = useSelector(
    (s) => s.inventoriIntelligent,
  )
  let [page, setPage] = useState({ page: tag.page })
  const [sortColumn, setSortColumn] = useState('name')
  const [sortDirection, setSortDirection] = useState('asc')
  const [search, setSearch] = useState('')
  const [searchTemp, setSearchTemp] = useState('')

  const { ownerData } = useSelector((s) => s.dashboard)

  const { data: itemsData } = useFetchWarehouseNetworkItemsQuery({
    page: 0,
    size: 999,
  })

  const {
    data: inventoriData,
    isLoading,
    isFetching,
  } = useFetchInventoriIntelligentQuery(
    {
      page: tag?.page,
      sortby: sortColumn,
      sortdir: sortDirection,
      size: tag?.size,
      s: searchTemp,
      owned: ownershipFilter?.value,
      itemIds:
        itemsFilter?.length > 0
          ? `[${itemsFilter.map((item) => item.value).join(',')}]`
          : undefined,
    },
    { skip: !page },
  )

  const data = inventoriData

  const columns = [
    {
      name: <span>Nama Gudang</span>,
      sortable: true,
      sortField: 'name',
      minWidth: '200px',
      left: true,
      cell: (row) => {
        return <span>{row?.name}</span>
      },
    },
    {
      name: <span>Akun/Pemilik</span>,
      sortable: true,
      sortField: 'ownerName',
      minWidth: '150px',
      left: true,
      cell: (row) => {
        return <span>{row?.ownerName || '-'}</span>
      },
    },
    {
      name: <span>Total Jenis Barang</span>,
      minWidth: '150px',
      maxWidth: '200px',
      sortable: true,
      sortField: 'totalItems',
      right: true,
      cell: (row) => {
        return <span>{newFormatThousandSeparator(row?.totalItems || 0)}</span>
      },
    },
    {
      name: <span>Total Inventory</span>,
      minWidth: '150px',
      maxWidth: '200px',
      sortable: true,
      sortField: 'totalInventory',
      right: true,
      cell: (row) => {
        return (
          <span>{newFormatThousandSeparator(row?.totalInventory || 0)}</span>
        )
      },
    },
    {
      name: <span>Jumlah</span>,
      minWidth: '120px',
      sortable: true,
      sortField: 'totalAmount',
      right: true,
      cell: (row) => {
        return (
          <span>{newFormatThousandSeparator(row?.totalAmount || 0)} Unit+</span>
        )
      },
    },
    {
      name: <span>Nilai</span>,
      minWidth: '120px',
      sortable: true,
      sortField: 'totalValue',
      right: true,
      cell: (row) => {
        return <span>Rp{newFormatThousandSeparator(row?.totalValue || 0)}</span>
      },
    },
    {
      name: '',
      minWidth: '80px',
      maxWidth: '100px',
      right: true,
      cell: (row) => {
        return (
          <div
            className="d-flex gap-3 align-items-center justify-content-end"
            data-testid={`inv-intel-table-row-${row?.id}`}
          >
            <LihatDetail
              onClick={() => {
                const ownerUserId =
                  Number(ownerData?.userId) !== Number(row?.userId)
                    ? row?.userId
                    : null

                navigate(
                  `${URL_INVENTORI_INTELLIGENCE}/${row?.id}${ownerUserId ? `?ownerUserId=${ownerUserId}` : ''}${row?.id == null ? (ownerUserId ? '&' : '?') + `userId=${row?.userId}` : ''}`,
                )
              }}
              id={row?.id}
              data-testid={`inv-intel-row-detail-${row?.id}`}
            />
          </div>
        )
      },
    },
  ]

  const handleSort = (column, direction) => {
    setSortColumn(column.sortField)
    setSortDirection(direction)
  }

  const handleSearch = (e) => {
    setSearch(e)
    if (fetchTimeoutRef.current) {
      clearTimeout(fetchTimeoutRef.current)
    }

    fetchTimeoutRef.current = setTimeout(() => {
      if (e.trim() !== '') {
        setSearchTemp(e)
      } else {
        setSearch('')
        setSearchTemp('')
      }
    }, 1000)
  }

  const handleRemoveItemFilter = (itemValue) => {
    dispatch(removeItemFilter(itemValue))
    dispatch(setFetchOption({ page: 0, size: tag?.size || 10 }))
  }

  return (
    <div className="w-100 px-3 pb-3">
      <div className="d-flex align-items-start gap-2 pt-3">
        <BreadCrumbs breadCrumbActive={'List Inventory Intelligence'} />
      </div>
      <div className="d-flex flex-column gap-2 mb-2">
        <Card className="border-0 shadow-sm">
          <CardBody>
            <DataTable
              columns={columns}
              data={data?.items}
              progressPending={isFetching || isLoading}
              responsive
              persistTableHead
              defaultSortFieldId={1}
              defaultSortAsc={true}
              sortIcon={<ChevronDown />}
              sortServer
              noDataComponent={
                <p className="my-3 fw-semibold">
                  Belum ada data inventory intelligence
                </p>
              }
              onSort={handleSort}
              pagination
              paginationServer
              paginationComponent={(props) => (
                <PMBPagination
                  rowCount={data?.totalItems || 0}
                  currentPage={tag?.page}
                  rowsPerPage={tag?.size}
                  setCurrentPage={(e) => {
                    dispatch(setFetchOption({ page: e }))
                  }}
                  setRowsPerPage={(e) => {
                    dispatch(setFetchOption({ size: e }))
                  }}
                  props={props}
                />
              )}
              subHeader={true}
              subHeaderComponent={
                <div className="w-100">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h5>List Inventory Intelligence</h5>
                  </div>

                  <div className="row justify-content-start">
                    <div className="col-lg-3 col-md-4 mb-3">
                      <p className="mb-2">Cari</p>
                      <InputGroup style={{ borderColor: '#D0D5DD' }}>
                        <InputGroupText
                          style={{
                            backgroundColor: 'white',
                            borderRightColor: 'white',
                          }}
                        >
                          <Search color="#667085" size={16} />
                        </InputGroupText>
                        <Input
                          placeholder="Cari nama gudang atau pemilik..."
                          onChange={(e) => handleSearch(e.target.value)}
                          data-testid="inv-intel-search-input"
                        />
                      </InputGroup>
                    </div>
                    <div className="col-lg-2 col-md-3 mb-3">
                      <PMBSelect
                        title="Kepemilikan"
                        value={ownershipFilter}
                        onChange={(value) => {
                          dispatch(setOwnershipFilter(value))
                          dispatch(
                            setFetchOption({ page: 0, size: tag?.size || 10 }),
                          )
                        }}
                        options={[
                          { label: 'Milik Sendiri', value: 1 },
                          { label: 'Milik Partner', value: 0 },
                        ]}
                        placeholder="Pilih..."
                        isClearable
                        testId="inv-intel-filter-ownership-select"
                      />
                    </div>
                    <div className="col-lg-3 col-md-4 mb-3">
                      <PMBSelect
                        title="Jenis Barang"
                        value={itemsFilter}
                        onChange={(value) => {
                          dispatch(setItemsFilter(value))
                          dispatch(
                            setFetchOption({ page: 0, size: tag?.size || 10 }),
                          )
                        }}
                        options={
                          itemsData?.items?.map((item) => ({
                            label: `${item?.nameItem || '-'}`,
                            value: item?.id,
                            image: item?.linkFoto,
                          })) || []
                        }
                        placeholder="Pilih items..."
                        isClearable
                        isMulti
                        maxMenuHeight={300}
                        menuPosition="fixed"
                        menuPlacement="bottom"
                        testId="inv-intel-filter-item-select"
                      />
                    </div>
                  </div>

                  {/* FILTER JENIS BARANG BADGES */}
                  {itemsFilter?.length > 0 && (
                    <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                      <span
                        className="text-muted"
                        style={{ fontSize: '0.8125rem', whiteSpace: 'nowrap' }}
                      >
                        Filter jenis barang:
                      </span>
                      {itemsFilter.map((item) => (
                        <Badge
                          key={item.value}
                          color=""
                          pill
                          className="d-inline-flex align-items-center gap-1 font-medium"
                          style={{
                            backgroundColor: '#0369a1',
                            color: '#fff',
                            fontSize: '0.8125rem',
                            fontWeight: 500,
                            border: '1px solid #C7D2FE',
                            lineHeight: 1.4,
                          }}
                        >
                          {item.label}
                          <button
                            type="button"
                            onClick={() => handleRemoveItemFilter(item.value)}
                            aria-label={`Hapus filter ${item.label}`}
                            style={{
                              background: 'none',
                              border: 'none',
                              padding: 0,
                              cursor: 'pointer',
                              display: 'inline-flex',
                              alignItems: 'center',
                              color: '#fff',
                              marginLeft: '2px',
                            }}
                          >
                            <X size={13} strokeWidth={2.5} />
                          </button>
                        </Badge>
                      ))}
                    </div>
                  )}
                  {/* END FILTER JENIS BARANG BADGES */}
                </div>
              }
            />
          </CardBody>
        </Card>
      </div>
    </div>
  )
}

export default InventoriIntelligent
